import { z } from "zod";
import type { SimpleTableConfig, SimpleFieldConfig } from "../UniversalCRMTable/types";
import { technicalIdentifierSchema } from "@wtc/technical-identifiers";

/**
 * Build a Zod validation schema from form field configuration.
 * Pure function: config → Zod schema with proper typing.
 *
 * @param config - The table configuration with field definitions
 * @param validationSchema - Optional custom validation schema to use instead of auto-generated
 * @returns A properly typed ZodObject schema
 */
export function buildFormSchema(
  config: SimpleTableConfig<any>,
  validationSchema?: z.ZodTypeAny
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  if (validationSchema) {
    // If custom schema is provided, validate it's a ZodObject
    if (validationSchema instanceof z.ZodObject) {
      return validationSchema;
    }
    // If it's not a ZodObject, return it with proper typing
    return validationSchema as z.ZodObject<Record<string, z.ZodTypeAny>>;
  }

  const schemaFields: Record<string, z.ZodTypeAny> = {};

  Object.entries(config.fields).forEach(([fieldName, field]) => {
    let fieldSchema: z.ZodTypeAny;

    // Check if field has custom validation
    if (field.validation?.custom) {
      fieldSchema = z.any().refine(field.validation.custom, {
        message: `Invalid ${field.label.toLowerCase()}`,
      });
    } else {
      // Default validation based on field type
      switch (field.type) {
        case "text":
        case "textarea":
          if (field.validation?.min !== undefined || field.validation?.max !== undefined) {
            // For text fields with numeric validation (like hours), validate as number
            fieldSchema = z.string().refine(
              (val) => {
                if (!val || val.trim() === "") return !field.required;
                const numVal = Number(val);
                if (isNaN(numVal)) return false;
                if (field.validation?.min !== undefined && numVal < field.validation.min)
                  return false;
                if (field.validation?.max !== undefined && numVal > field.validation.max)
                  return false;
                return true;
              },
              {
                message:
                  field.validation?.min !== undefined && field.validation?.max !== undefined
                    ? `${field.label} must be between ${field.validation.min} and ${field.validation.max}`
                    : field.validation?.min !== undefined
                      ? `${field.label} must be at least ${field.validation.min}`
                      : `${field.label} must be no more than ${field.validation.max}`,
              }
            );
          } else {
            fieldSchema =
              field.required || field.validation?.required
                ? z
                    .string()
                    .min(
                      1,
                      field.validation?.required === true
                        ? `${field.label} is required`
                        : field.validation?.required || `${field.label} is required`
                    )
                : z.string().optional();
          }
          break;
        case "number":
          if (!field.required && !field.validation?.required) {
            // For optional number fields, allow undefined but reject NaN
            fieldSchema = z
              .union([
                z.number().refine((val) => !isNaN(val), {
                  message: "Invalid number",
                }),
                z.undefined(),
              ])
              .optional();
          } else {
            // For required number fields, reject both undefined and NaN
            // Apply min/max to ZodNumber first, then refine if needed
            let numberSchema: z.ZodNumber = z.number();
            if (field.validation?.min !== undefined)
              numberSchema = numberSchema.min(field.validation.min);
            if (field.validation?.max !== undefined)
              numberSchema = numberSchema.max(field.validation.max);
            // Refine for NaN check (though z.number() already validates this)
            fieldSchema = numberSchema.refine((val) => !isNaN(val), {
              message: `${field.label} must be a valid number`,
            });
          }
          break;
        case "boolean":
          fieldSchema = z.boolean().optional();
          break;
        case "date":
        case "datetime":
        case "time":
        case "due-date":
        case "date-worked":
          fieldSchema = z.string().optional();
          break;
        case "select":
        case "client-select":
        case "ticket-select":
        case "user":
          fieldSchema = z.string().optional();
          break;
        case "multi-user":
          fieldSchema = z.array(z.string()).optional();
          break;
        case "tags":
          fieldSchema = z.array(z.string()).optional();
          break;
        case "template-select":
          fieldSchema = z
            .custom<unknown>((val) => {
              if (val === null || val === undefined) return true;
              if (typeof val === "object" && "id" in val && "name" in val && "title" in val) {
                return true;
              }
              return false;
            })
            .optional();
          break;
        case "array":
          // technical_identifiers is an object array, not string[] — validate
          // each entry's shape (system/label/value/notes).
          if (field.customComponent === "TechnicalIdentifiersEditor") {
            fieldSchema = z.array(technicalIdentifierSchema).optional();
          } else {
            fieldSchema = z.array(z.string()).optional();
          }
          break;
        case "json":
          // Staff/hard-cost line items: array of objects
          if ((field as { lineItemsType?: string }).lineItemsType) {
            fieldSchema = z.array(z.any()).optional();
          } else {
            fieldSchema = z.any().optional();
          }
          break;
        default:
          fieldSchema = z.string().optional();
      }
    }

    schemaFields[fieldName] = fieldSchema;
  });

  return z.object(schemaFields);
}
